// Enterprise Case Studies - Detailed with Metrics
import React from 'react';
import { Truck, Home, TrendingUp, Clock, DollarSign, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Link } from 'react-router-dom';

interface CaseStudy {
  id: string;
  industry: string;
  client: string;
  icon: React.ReactNode;
  challenge: string;
  solution: string;
  results: { metric: string; value: string; description: string }[];
  techStack: string[];
  timeline: string;
  color: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 'logistics-ai',
    industry: 'Logistics & Supply Chain',
    client: 'Global Logistics Provider',
    icon: <Truck className="w-8 h-8" />,
    challenge: 'Manual route planning caused 40% fleet inefficiency and rising fuel costs. Dispatchers spent hours optimizing routes that became outdated within minutes due to traffic changes.',
    solution: 'AI-powered route optimization using real-time traffic data, weather patterns, and delivery windows. Machine learning models predict optimal routes and automatically adjust for disruptions.',
    results: [
      { metric: '32%', value: 'Fuel Cost Reduction', description: 'Annual savings of $2.4M' },
      { metric: '2x', value: 'Faster Deliveries', description: 'Average route time halved' },
      { metric: '4 mo', value: 'ROI Achieved', description: 'Full payback in 4 months' },
    ],
    techStack: ['Python', 'TensorFlow', 'React', 'AWS', 'PostgreSQL'],
    timeline: '14 weeks from kickoff to production',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'property-crm',
    industry: 'Real Estate Technology',
    client: 'PropertyAgent CRM',
    icon: <Home className="w-8 h-8" />,
    challenge: 'Real estate agents losing deals due to poor pipeline visibility and manual follow-ups. No mobile access meant missed opportunities in the field.',
    solution: 'AI-powered CRM with risk alerts, visual pipeline management, and mobile-first PWA. Intelligent lead scoring prioritizes high-potential deals automatically.',
    results: [
      { metric: '30%', value: 'Higher Closing Rate', description: 'More deals closed per agent' },
      { metric: '50%', value: 'Less Admin Time', description: 'Automated data entry & follow-ups' },
      { metric: '100%', value: 'Mobile Access', description: 'Full functionality on any device' },
    ],
    techStack: ['React', 'TypeScript', 'Supabase', 'AI/ML', 'PWA'],
    timeline: '12 weeks from concept to launch',
    color: 'from-violet-500 to-purple-500',
  },
];

const CaseStudyCard = ({ study, index, isVisible }: { study: CaseStudy; index: number; isVisible: boolean }) => {
  const isEven = index % 2 === 0;
  
  return (
    <div 
      className="relative"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${index * 200}ms`,
      }}
    >
      <div 
        className="group/card rounded-3xl border border-white/[0.08] bg-[#111111] overflow-hidden hover:border-white/[0.15] transition-all duration-500"
        style={{
          boxShadow: '0 8px 40px -12px rgba(0, 0, 0, 0.6)',
        }}
      >
        {/* Header */}
        <div className={`relative p-8 lg:p-10 bg-gradient-to-br ${study.color} bg-opacity-10`}>
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              background: `linear-gradient(135deg, transparent 0%, rgba(0,0,0,0.3) 100%)`,
            }}
          />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${study.color} flex items-center justify-center text-white`}>
                  {study.icon}
                </div>
                <div>
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider">{study.industry}</p>
                  <h3 className="text-2xl font-bold text-white">{study.client}</h3>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Clock className="w-4 h-4" />
              {study.timeline}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-8 lg:p-10">
          {/* Challenge & Solution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            <div>
              <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">The Challenge</h4>
              <p className="text-white/70 leading-relaxed">{study.challenge}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-3">The Solution</h4>
              <p className="text-white/70 leading-relaxed">{study.solution}</p>
            </div>
          </div>
          
          {/* Results */}
          <div className="mb-10">
            <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-6">Results Delivered</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {study.results.map((result, i) => (
                <div 
                  key={result.value}
                  className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center hover:border-primary/30 hover:bg-white/[0.04] transition-all duration-300"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
                    transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${(index * 200) + (i * 100) + 400}ms`,
                  }}
                >
                  <p className="text-4xl lg:text-5xl font-bold text-primary mb-1">{result.metric}</p>
                  <p className="text-sm font-semibold text-white mb-1">{result.value}</p>
                  <p className="text-xs text-white/50">{result.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Tech Stack */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">Tech:</span>
            {study.techStack.map((tech) => (
              <span 
                key={tech}
                className="px-3 py-1.5 text-xs font-medium text-white/60 bg-white/[0.04] rounded-lg border border-white/[0.06]"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const EnterpriseCaseStudies = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.05 });
  
  return (
    <section 
      id="case-studies"
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a]"
    >
      {/* Background accent */}
      <div 
        className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, hsl(var(--primary) / 0.03) 0%, transparent 60%)',
        }}
      />
      
      <div className="container-professional relative z-10">
        {/* Header */}
        <div 
          className="text-center mb-16 lg:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
            <TrendingUp className="w-4 h-4" />
            Proven Results
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            Enterprise{' '}
            <span className="text-primary">Case Studies</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Real projects. Measurable outcomes. See how we've helped enterprises 
            transform their operations with AI and custom software.
          </p>
        </div>
        
        {/* Case Studies */}
        <div className="space-y-8 lg:space-y-12">
          {caseStudies.map((study, index) => (
            <CaseStudyCard 
              key={study.id}
              study={study}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
        
        {/* CTA */}
        <div 
          className="mt-16 text-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 600ms',
          }}
        >
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-primary hover:text-white font-medium transition-colors"
          >
            View All Case Studies
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
